/**
 * Coaching Hooks
 * React Query mutations and queries for coaching sessions
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  createCoachingSession,
  deleteCoachingSession,
  getCoachingSessionById,
  listCoachingSessionsPublic,
  listMyCoachingSessions,
  publishCoachingSession,
  updateCoachingSession,
} from "../services/coaching.service"

export function useCreateCoachingSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Parameters<typeof createCoachingSession>[0]) => createCoachingSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-coaching-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["coaching-sessions-public"] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics"] })
    },
  })
}

export function useMyCoachingSessions(
  userId: string,
  options?: {
    page?: number
    limit?: number
    search?: string
    enabled?: boolean
    status?: "all" | "live" | "draft"
    includePaused?: boolean
  },
) {
  return useQuery({
    queryKey: [
      "user-coaching-sessions",
      userId,
      options?.page,
      options?.limit,
      options?.search,
      options?.status,
      options?.includePaused,
    ],
    queryFn: () =>
      listMyCoachingSessions({
        userId,
        page: options?.page,
        limit: options?.limit,
        search: options?.search,
        status: options?.status,
        includePaused: options?.includePaused,
      }),
    enabled: options?.enabled ?? !!userId,
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Public browse hook for coaching sessions
 * Fetches all live coaching sessions without user filtering
 */
export function useBrowseCoachingSessions(options?: {
  page?: number
  limit?: number
  search?: string
  enabled?: boolean
}) {
  return useQuery({
    queryKey: ["coaching-sessions-public", options?.page, options?.limit, options?.search],
    queryFn: () =>
      listCoachingSessionsPublic({
        page: options?.page,
        limit: options?.limit,
        search: options?.search,
      }),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
  })
}

export function useCoachingSessionById(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["coaching-session", id],
    queryFn: () => getCoachingSessionById(id),
    enabled: options?.enabled ?? !!id,
    staleTime: 1000 * 60 * 5,
  })
}

export function useUpdateCoachingSession(coachingId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Parameters<typeof updateCoachingSession>[1]) =>
      updateCoachingSession(coachingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coaching-session", coachingId] })
      queryClient.invalidateQueries({ queryKey: ["user-coaching-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["coaching-sessions-public"] })
      queryClient.invalidateQueries({ queryKey: ["saved-coaching-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics"] })
    },
  })
}

export function useDeleteCoachingSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteCoachingSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-coaching-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["coaching-sessions-public"] })
      queryClient.invalidateQueries({ queryKey: ["saved-coaching-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics"] })
    },
  })
}

export function usePublishCoachingSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => publishCoachingSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coaching-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["coaching-session"] })
      queryClient.invalidateQueries({ queryKey: ["user-coaching-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["coaching-sessions-public"] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics"] })
      toast.success("Session published successfully!")
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error?.response?.data?.message || "Failed to publish session")
    },
  })
}
